// src/hooks/useMyCoast.js
// React Hook for managing MyCoast real data only - 清理版本

import { useState, useEffect, useCallback } from 'react';
import { fetchMyCoastData, findNearbyImages } from '../utils/myCoastAPI';

/**
 * Custom hook for managing MyCoast data (real data only)
 * @returns {Object} MyCoast data and functions
 */
export const useMyCoast = () => {
  const [myCoastData, setMyCoastData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Cache duration: 30 minutes
  const CACHE_DURATION = 30 * 60 * 1000;

  /**
   * Load MyCoast data from API - 清理版本
   */
  const loadMyCoastData = useCallback(async (forceRefresh = false) => {
    // Check if we need to refresh the cache
    if (!forceRefresh && lastFetched && myCoastData.length > 0) {
      const timeSinceLastFetch = Date.now() - lastFetched;
      if (timeSinceLastFetch < CACHE_DURATION) {
        console.log('📸 Using cached MyCoast data');
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('📸 Fetching real MyCoast data...');
      const data = await fetchMyCoastData(2); // Limit to 2 pages for performance
      
      if (data && data.length > 0) {
        setMyCoastData(data);
        setLastFetched(Date.now());
        console.log(`✅ Successfully loaded ${data.length} real MyCoast images`);
      } else {
        console.log('📸 No real MyCoast images found');
        setMyCoastData([]);
        setLastFetched(Date.now());
      }
    } catch (err) {
      console.error('❌ Failed to load MyCoast data:', err);
      setError(err.message || 'Failed to load MyCoast data');
      setMyCoastData([]); // 🔥 关键修改：失败时设置为空数组
    } finally {
      setIsLoading(false);
    }
  }, []); // 移除依赖以避免无限循环

  /**
   * Get nearby images for a specific sensor location
   */
  const getNearbyImages = useCallback(async (sensorCoordinates, radiusKm = 2, maxResults = 6) => {
    if (!myCoastData.length || !sensorCoordinates) {
      console.log('📸 No MyCoast data available for nearby search');
      return [];
    }

    try {
      const nearbyImages = await findNearbyImages(
        myCoastData, 
        sensorCoordinates, 
        radiusKm, 
        maxResults
      );
      
      if (nearbyImages.length > 0) {
        console.log(`📸 Found ${nearbyImages.length} real nearby images within ${radiusKm}km`);
      } else {
        console.log(`📸 No real MyCoast images found within ${radiusKm}km`);
      }
      
      return nearbyImages;
    } catch (err) {
      console.error('❌ Error finding nearby images:', err);
      return [];
    }
  }, [myCoastData]);

  /**
   * Initialize data loading on mount
   */
  useEffect(() => {
    loadMyCoastData();
  }, [loadMyCoastData]);

  return {
    myCoastData,
    isLoading,
    error,
    loadMyCoastData,
    getNearbyImages,
    hasData: myCoastData.length > 0,
    lastFetched: lastFetched ? new Date(lastFetched) : null,
    // 新增调试信息
    dataSource: myCoastData.length > 0 ? 'real-mycoast' : 'none'
  };
};

/**
 * Hook specifically for getting images near a sensor
 * @param {Object} sensor - The selected sensor object
 * @param {number} radiusKm - Search radius in kilometers  
 * @param {number} maxResults - Maximum number of results
 * @returns {Object} Nearby images data and loading state
 */
export const useNearbyImages = (sensor, radiusKm = 2, maxResults = 6) => {
  const { myCoastData, isLoading: dataLoading, getNearbyImages, dataSource } = useMyCoast();
  const [nearbyImages, setNearbyImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  useEffect(() => {
    const loadNearbyImages = async () => {
      if (!sensor || !sensor.coordinates || !myCoastData.length) {
        setNearbyImages([]);
        return;
      }

      setIsLoadingImages(true);
      try {
        const images = await getNearbyImages(sensor.coordinates, radiusKm, maxResults);
        setNearbyImages(images);
        
        // 调试日志
        if (images.length > 0) {
          console.log(`📸 Loaded ${images.length} real MyCoast images for sensor ${sensor.id}`);
        } else {
          console.log(`📸 No real MyCoast images found near sensor ${sensor.id}`);
        }
      } catch (error) {
        console.error('Error loading nearby images:', error);
        setNearbyImages([]);
      } finally {
        setIsLoadingImages(false);
      }
    };

    loadNearbyImages();
  }, [sensor, myCoastData, getNearbyImages, radiusKm, maxResults]);

  return {
    nearbyImages,
    isLoading: dataLoading || isLoadingImages,
    hasImages: nearbyImages.length > 0,
    imageCount: nearbyImages.length,
    dataSource // 返回数据源信息用于调试
  };
};

export default useMyCoast;